import type {
  FieldTrend,
  MyDashboard,
  OrgDashboard,
  TrendBucket,
} from '../types';

// Aggregate dashboard fixtures (HUB-504), ported from the prototype. These are the
// ONLY surfaces where agent-vs-coder provenance is legitimately used (in aggregate,
// never per-case in a way the auditor could tie back to a live review). Not shown
// in slice 1 — landed here so the data foundation is complete and the source's
// dashboard fetchers have something to return.

/**
 * The three fields the preview slices its trend buckets by. A subset of the
 * server's scored set on purpose — the fixture is a preview, and three chips
 * is enough to show the control working.
 */
const PREVIEW_FIELDS = ['surgical_cpt', 'anesthesia_cpt', 'primary_icd'];

/**
 * Field i's share of `total`, remainder to the earliest fields.
 *
 * Deterministic, and the three shares sum to `total` EXACTLY — that identity
 * (a bucket's field slices reconstruct its totals) is what the real fold
 * guarantees, and a preview that quietly broke it would teach a reader to stop
 * checking the charts against each other.
 */
const share = (total: number, i: number) =>
  Math.floor(total / PREVIEW_FIELDS.length) +
  (i < total % PREVIEW_FIELDS.length ? 1 : 0);

/**
 * Per-field slices of one bucket.
 *
 * The verdict counters are split out of the bucket's own, so an UNMEASURED
 * bucket (whose totals are all zero) yields zero for every field — the floor
 * applies to the bucket, and a field never publishes a verdict the total does
 * not. The corpus counters are not floored: each field's four outcomes account
 * for every comparable case exactly once, on measured and unmeasured buckets
 * alike.
 */
function previewFields(b: Omit<TrendBucket, 'fields'>): FieldTrend[] {
  return PREVIEW_FIELDS.map((field, i) => {
    const agentCorrect = share(b.agentCorrect, i);
    const coderCorrect = share(b.coderCorrect, i);
    const both = share(b.both, i);
    const neither = share(b.neither, i);
    const differ = Math.min(1 + i, b.comparable);
    const oneSided = Math.min(i, Math.max(b.comparable - differ, 0));
    const agree = Math.max(b.comparable - differ - oneSided, 0);
    return {
      field,
      judged: agentCorrect + coderCorrect + both + neither,
      agentCorrect,
      coderCorrect,
      both,
      neither,
      agree,
      differ,
      oneSided,
      notComparable: b.comparable - agree - differ - oneSided,
    };
  });
}

/** A bucket plus its per-field slices. */
function withFields(b: Omit<TrendBucket, 'fields'>): TrendBucket {
  return { ...b, fields: previewFields(b) };
}

/**
 * Weekly trend buckets, Mondays back from 2026-08-31 (the fixture's "this
 * week"). Reviewed volume and both sides' accuracy climb steadily week over
 * week, so the preview shows the rising line a real study's ramp-up produces.
 */
function weekly(n: number): TrendBucket[] {
  const anchor = Date.UTC(2026, 7, 31); // Monday, 2026-08-31
  const buckets: TrendBucket[] = [];
  for (let i = 0; i < n; i++) {
    const weeksAgo = n - 1 - i;
    const start = new Date(anchor - weeksAgo * 7 * 86400000)
      .toISOString()
      .slice(0, 10);
    // 12..20 across the window, rising with i.
    const reviewed = 12 + Math.round((i / Math.max(n - 1, 1)) * 8);
    const agentCorrect = 8 + i;
    const coderCorrect = 7 + i;
    const both = 2;
    const neither = 1;
    buckets.push(
      withFields({
        start,
        casesReceived: reviewed + 6,
        comparable: reviewed + 3,
        fullyAgreed: both + 1,
        reviewed,
        measured: true,
        judged: agentCorrect + coderCorrect + both + neither,
        agentCorrect,
        coderCorrect,
        both,
        neither,
      }),
    );
  }
  return buckets;
}

/**
 * Daily trend buckets back from 2026-09-06 (the fixture's "today"). Every
 * third bucket is a review-floor gap — reviewed but not YET measured (too few
 * distinct reviewers) — so the preview shows the chart rendering a gap rather
 * than a false 0%, same as a real under-reviewed day would.
 */
function daily(n: number): TrendBucket[] {
  const anchor = Date.UTC(2026, 8, 6); // 2026-09-06
  const buckets: TrendBucket[] = [];
  for (let i = 0; i < n; i++) {
    const daysAgo = n - 1 - i;
    const start = new Date(anchor - daysAgo * 86400000)
      .toISOString()
      .slice(0, 10);
    if (i % 3 === 2) {
      buckets.push(
        withFields({
          start,
          casesReceived: 3,
          comparable: 1,
          fullyAgreed: 0,
          reviewed: 1,
          measured: false,
          judged: 0,
          agentCorrect: 0,
          coderCorrect: 0,
          both: 0,
          neither: 0,
        }),
      );
      continue;
    }
    const reviewed = 8 + (i % 5);
    const agentCorrect = 3 + (i % 3);
    const coderCorrect = 2 + (i % 2);
    const both = 1;
    const neither = Math.max(reviewed - agentCorrect - coderCorrect - both, 0);
    buckets.push(
      withFields({
        start,
        casesReceived: reviewed + 3,
        comparable: reviewed + 1,
        fullyAgreed: both,
        reviewed,
        measured: true,
        judged: agentCorrect + coderCorrect + both + neither,
        agentCorrect,
        coderCorrect,
        both,
        neither,
      }),
    );
  }
  return buckets;
}

export const AUDIT_DASHBOARD: OrgDashboard = {
  reviewed: 342,
  thisWeek: 61,
  judged: 420,
  both: 230,
  neither: 34,
  excluded: 0,
  // Rates on the SERVER's scale — [0,1], not percent. The fixture previously
  // carried 71.6/88.4/85.1 while the live mapper passes the wire value through
  // untouched, so whichever scale the view assumed, the other source rendered
  // 100x off. The type says [0,1]; the server is authoritative.
  agreement: 0.716,
  agentAccuracy: 0.884,
  coderAccuracy: 0.851,
  // Selection match sits ABOVE accuracy here, and that is the expected shape,
  // not a fixture typo: its denominator drops the 34 judgements where the
  // auditor rejected both sides without naming a replacement.
  selectionResolved: 386,
  selectionUnresolved: 34,
  agentSelectionMatch: 0.902,
  coderSelectionMatch: 0.869,
  erroredCodes: [
    {
      code: '00790', field: 'anesthesia_cpt', agentWrong: 14, coderWrong: 3, wrong: 17,
      reasons: [
        { reason: 'proc_detail_off', count: 11 },
        { reason: 'doc_insufficient', count: 6 },
      ],
      exampleDcns: ['AC-100482', 'AC-100517', 'AC-100603'],
    },
    {
      code: 'QK', field: 'modifiers', agentWrong: 9, coderWrong: 4, wrong: 13,
      reasons: [
        { reason: 'mod_wrong_direction', count: 10 },
        { reason: 'doc_insufficient', count: 3 },
      ],
      exampleDcns: ['AC-100488', 'AC-100521', 'AC-100544'],
    },
    {
      code: '45378', field: 'surgical_cpt', agentWrong: 4, coderWrong: 7, wrong: 11,
      reasons: [
        { reason: 'proc_detail_off', count: 8 },
        { reason: 'proc_misidentified', count: 3 },
      ],
      exampleDcns: ['AC-100495', 'AC-100530', 'AC-100558'],
    },
    {
      code: 'Z12.11', field: 'additional_icd', agentWrong: 6, coderWrong: 0, wrong: 6,
      reasons: [
        { reason: 'icd_specificity', count: 4 },
        { reason: 'human_error', count: 2 },
      ],
      exampleDcns: ['AC-100501', 'AC-100509', 'AC-100612'],
    },
  ],
  erroredCodesSuppressed: 21,
  erroredCodesCapped: 0,
  avgReviewMin: '7m 24s',
  fields: [
    {
      field: 'surgical_cpt',
      agentCorrect: 41,
      coderCorrect: 38,
      both: 112,
      neither: 19,
      judged: 210,
      topReasonCounts: [
        { reason: 'proc_detail_off', count: 19, codes: [{ field: 'surgical_cpt', code: '45378', count: 8 }] },
        { reason: 'proc_misidentified', count: 11, codes: [{ field: 'surgical_cpt', code: '45378', count: 3 }] },
        { reason: 'proc_bundling', count: 7, codes: [] },
        { reason: 'doc_insufficient', count: 4, codes: [{ field: 'surgical_cpt', code: '45380', count: 4 }] },
      ],
    },
    {
      field: 'primary_icd',
      agentCorrect: 33,
      coderCorrect: 44,
      both: 118,
      neither: 15,
      judged: 210,
      topReasonCounts: [
        { reason: 'icd_specificity', count: 24, codes: [{ field: 'primary_icd', code: 'K63.5', count: 4 }] },
        { reason: 'icd_wrong', count: 9, codes: [] },
        { reason: 'doc_insufficient', count: 6, codes: [] },
      ],
    },
  ],
  topReasons: [
    { reason: 'mod_wrong_direction', count: 31, codes: [{ field: 'modifiers', code: 'QK', count: 10 }] },
    { reason: 'icd_specificity', count: 24, codes: [{ field: 'additional_icd', code: 'Z12.11', count: 4 }] },
    { reason: 'proc_detail_off', count: 19, codes: [{ field: 'anesthesia_cpt', code: '00790', count: 11 }, { field: 'surgical_cpt', code: '45378', count: 8 }] },
    { reason: 'human_error', count: 12, codes: [{ field: 'additional_icd', code: 'Z12.11', count: 2 }] },
  ],
  trend: {
    daily: daily(30),
    weekly: weekly(8),
    reviewsDaily: daily(30).map((b) => ({ start: b.start, reviewsSubmitted: b.reviewed })),
    reviewsWeekly: weekly(8).map((b) => ({ start: b.start, reviewsSubmitted: b.reviewed })),
  },
  // Corpus agreement — measured between the two coded results, no reviewer
  // input. The exclusion set is deliberately large here: on a real study most
  // cases are not comparable yet, and a fixture that hid that would make the
  // view look tested against a shape it will never see.
  corpus: {
    cases: 1412,
    comparable: 486,
    fullyAgreed: 209,
    exclusions: [
      { reason: 'only one side coded so far', cases: 703 },
      { reason: 'not billable', cases: 148 },
      { reason: 'no comparable result produced', cases: 61 },
      { reason: 'duplicate DCN (two episodes claim it)', cases: 14 },
    ],
    fields: [
      { field: 'surgical_cpt', agree: 341, differ: 118, oneSided: 27, notComparable: 0 },
      { field: 'anesthesia_cpt', agree: 372, differ: 96, oneSided: 18, notComparable: 0 },
      { field: 'primary_icd', agree: 268, differ: 186, oneSided: 32, notComparable: 0 },
      { field: 'additional_icd', agree: 190, differ: 296, oneSided: 0, notComparable: 0 },
      // Modifiers are inferred on one side and only recorded when documented on
      // the other, so most cases are unscorable rather than in disagreement.
      { field: 'modifiers', agree: 31, differ: 12, oneSided: 0, notComparable: 443 },
      { field: 'phys_status', agree: 402, differ: 84, oneSided: 0, notComparable: 0 },
    ],
    codes: [
      { field: 'surgical_cpt', code: '45378', seen: 118, matched: 96, exampleDcns: ['AC-100482', 'AC-100517', 'AC-100603'] },
      { field: 'anesthesia_cpt', code: '00810', seen: 104, matched: 91, exampleDcns: ['AC-100488', 'AC-100521'] },
      { field: 'primary_icd', code: 'Z12.11', seen: 87, matched: 44, exampleDcns: ['AC-100495', 'AC-100530', 'AC-100612'] },
      { field: 'surgical_cpt', code: '43239', seen: 71, matched: 63, exampleDcns: ['AC-100501', 'AC-100544'] },
      { field: 'primary_icd', code: 'K63.5', seen: 66, matched: 29, exampleDcns: ['AC-100509', 'AC-100558'] },
    ],
    codesSuppressed: 212,
  },
  // The fixture's reviewed count (342) is well past the floor, from a study
  // with many reviewers — the shape the preview screen should normally show.
  verdictFloor: { cleared: true, minReviews: 3, minReviewers: 2 },
};

export const AUDITOR_STATS: MyDashboard = {
  weekLabel: 'Jun 8 – Jun 12, 2026',
  reviewedWeek: 47,
  reviewedTarget: 60,
  reviewedLastWeek: 41,
  reviewedToday: 9,
  avgReviewMin: '6m 52s',
  avgLastWeek: '7m 40s',
  queueRemaining: 9,
  agreementRate: 0.734,
  submittedTotal: 342,
  dayLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  dayCounts: [11, 9, 8, 10, 9],
  dayTarget: 12,
  verdictMix: { both: 112, a: 41, b: 38, neither: 19 },
  fieldsFlagged: [
    { label: 'Modifiers', count: 18 },
    { label: 'Physical status', count: 14 },
    { label: 'Primary diagnosis', count: 12 },
    { label: 'Additional diagnoses', count: 11 },
    { label: 'Surgical CPT', count: 7 },
  ],
  topReasons: [
    { label: 'Wrong provider/direction modifier', count: 15 },
    { label: 'Wrong physical status level (P1–P6)', count: 12 },
    { label: 'Human coder error (agent was correct)', count: 11 },
    { label: 'Insufficient specificity', count: 9 },
    { label: 'Missing code supported by documentation', count: 7 },
  ],
  recent: [
    { id: 'AC-100533', patient: 'Silva, G.', when: 'Today · 10:24a', flags: 0 },
    { id: 'AC-100482', patient: 'Thompson, R.', when: 'Today · 9:48a', flags: 3 },
    { id: 'AC-100521', patient: 'Hughes, W.', when: 'Yesterday · 4:12p', flags: 1 },
    { id: 'AC-100538', patient: 'Brooks, D.', when: 'Yesterday · 2:39p', flags: 1 },
    { id: 'AC-100491', patient: 'Kim, H.', when: 'Yesterday · 1:05p', flags: 2 },
  ],
  streak: 12,
  rank: 2,
  teamSize: 9,
};
